import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserPushStatus, gameApi } from '../api';
import { applicationServerKey, getPushRegistration, pushUnavailableReason, readPushSubscriptionId, savePushSubscriptionId } from '../utils/browserPush';

export const PushNotificationSettings: React.FC<{ playerName: string }> = ({ playerName }) => {
  const [status, setStatus] = useState<BrowserPushStatus | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [blocked, setBlocked] = useState(false);
  const unavailable = pushUnavailableReason();
  const revision = useRef(0);
  const subscriptionId = useRef<string | null>(null);
  const invalidate = useCallback(() => { revision.current++; }, []);

  const refresh = useCallback(async () => {
    const current = ++revision.current;
    setBusy(true); setError(null); setMessage(''); setStatus(null);
    if (pushUnavailableReason()) { setBusy(false); return; }
    try {
      const id = readPushSubscriptionId(playerName);
      subscriptionId.current = id;
      const result = await gameApi.getBrowserPushStatus(id);
      if (current !== revision.current) return;
      setStatus(result);
      setBlocked(Notification.permission === 'denied');
      if (result.subscribed) {
        const registration = await getPushRegistration();
        const subscription = await registration.pushManager.getSubscription();
        if (current !== revision.current) return;
        // Retain the server registration ID for cleanup even when browser permission was revoked.
        if (!subscription || Notification.permission !== 'granted') setMessage('This browser’s push permission or subscription has changed. Unsubscribe, then subscribe again to restore alerts.');
      }
    } catch (err) {
      if (current === revision.current) setError(err instanceof Error ? err.message : 'Could not load push settings.');
    } finally { if (current === revision.current) setBusy(false); }
  }, [playerName]);

  useEffect(() => {
    void refresh();
    return invalidate;
  }, [refresh, invalidate]);

  const toggle = async () => {
    if (!status || busy) return;
    const current = revision.current;
    const isCurrent = () => current === revision.current;
    setBusy(true); setError(null); setMessage('');
    try {
      if (status.subscribed) {
        const id = subscriptionId.current;
        if (!id) throw new Error('Browser subscription could not be identified. Reopen Account and retry.');
        await gameApi.unsubscribeBrowserPush(id);
        if (!isCurrent()) return;
        const registration = await getPushRegistration();
        if (!isCurrent()) return;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription && !await subscription.unsubscribe()) throw new Error('Browser unsubscribe failed. Please retry.');
        savePushSubscriptionId(playerName, null);
        subscriptionId.current = null;
        if (isCurrent()) { setStatus({ ...status, subscribed: false }); setMessage('Push notifications unsubscribed in this browser.'); }
      } else {
        // Invoke permission directly from the button gesture, before network/service-worker awaits.
        const permission = await Notification.requestPermission();
        if (!isCurrent()) return;
        setBlocked(permission === 'denied');
        if (permission !== 'granted') throw new Error(permission === 'denied'
          ? 'Notifications are blocked. Allow them in your browser’s site settings, then retry.'
          : 'Notification permission was not granted.');
        if (!status.publicKey) throw new Error('Push notifications are not configured.');
        const registration = await getPushRegistration();
        if (!isCurrent()) return;
        let subscription = await registration.pushManager.getSubscription();
        const expectedKey = applicationServerKey(status.publicKey);
        const actualKey = subscription?.options.applicationServerKey;
        if (subscription && actualKey && Array.from(new Uint8Array(actualKey)).join(',') !== Array.from(expectedKey).join(',')) {
          await subscription.unsubscribe(); subscription = null;
        }
        subscription = subscription || await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: expectedKey });
        if (!isCurrent()) return;
        const result = await gameApi.subscribeBrowserPush(subscription.toJSON());
        if (isCurrent()) {
          subscriptionId.current = result.subscriptionId;
          setStatus({ ...status, subscribed: true });
        }
        savePushSubscriptionId(playerName, result.subscriptionId);
        if (isCurrent()) { setStatus({ ...status, subscribed: true }); setMessage('Push notifications subscribed in this browser.'); }
      }
    } catch (err) {
      if (isCurrent()) setError(err instanceof Error ? err.message : 'Could not update push settings.');
    } finally { if (isCurrent()) setBusy(false); }
  };

  return <div className="settings-field push-notification-settings">
    <button type="button" className="entry-secondary-action" disabled={busy || !!unavailable || !status || (!status.available && !status.subscribed)} onClick={() => { void toggle(); }}>
      {status?.subscribed ? 'Unsubscribe from push notifications' : 'Subscribe to push notifications'}
    </button>
    <div role="status" aria-label="Push notification status" aria-live="polite">{busy ? 'Loading push settings…' : message}</div>
    {unavailable && <div>{unavailable}</div>}
    {!unavailable && status && !status.available && <div>Push notifications are not configured on this server.</div>}
    {!unavailable && blocked && <div>Notifications are blocked. Allow them in your browser’s site settings.</div>}
    {error && <div className="error" role="alert">{error} <button type="button" onClick={() => { void refresh(); }}>Retry push settings</button></div>}
  </div>;
};
